import colourHelper from '../../helpers/colour';
import { EVENT_NAME_PASSWORD_INPUT, EVENT_STEP_VALIDATE } from '../../helpers/consts';
import { PasswordField } from "./passwordField";

const PasswordValidator = require('password-validator');
const entropy = require('string-entropy');

export class PasswordStrength {
    constructor(options) {
        this.passwordRules = {
            min: 'rule-min-active',
            lowercase: 'rule-lowercase-active',
            uppercase: 'rule-uppercase-active',
            digits: 'rule-digits-active',
        }
        this.passwordRuleMessages = {
            forbiddenPassword: __('Your password is too easy to guess'),
            noMatch: __('Passwords do not match'),
        }
        this.forbiddenPasswords = [
            'wachtwoord',
            'wachtwoord1',
            'wachtwoord12',
            'wachtwoord123',
            'Wachtwoord',
            'Wachtwoord1',
            'Wachtwoord12',
            'Wachtwoord123',
            'abc123',
            'abcdef',
            'qwerty',
            'asdf',
            '123456'
        ];
        this.strongPasswordEntropy = 100;
        this.store = options.store;
        this.state = this.store.getState();
        this.$context = options.$context;
        this.id = options.id;
        this.label = options.label;
        this.stepId = options.stepId
        this.isValid = false;
        this.$el = this.createFieldElement();
    }

    createFieldElement () { // eslint-disable-line class-methods-use-this
        const $passwordStrengthField = document.createElement('div');
        const $strengthBar = document.createElement('div');
        const firstPasswordField = new PasswordField({
            store: this.store,
            stepId: this.stepId,
            id: 'password',
            label: __('Password'),
            $context: $passwordStrengthField,
        });
        const auditPasswordField = new PasswordField({
            store: this.store,
            stepId: this.stepId,
            id: 'audit-password',
            label: __('Repeat password'),
            'audit': true,
            $context: $passwordStrengthField,
        });
        this.$passwordWrapper = document.createElement('div');
        this.$strengthBarTitle = document.createElement('p');
        this.$strengthBarProgress = document.createElement('div');
        this.$passwordCheckList = document.createElement('ul');
        this.$firstPasswordField = firstPasswordField.render();
        this.$auditPasswordField = auditPasswordField.render();
        this.$fieldMessage = document.createElement('span');

        this.$fieldMessage.classList.add('invalid-feedback');
        this.$fieldMessage.setAttribute('role', 'alert');

        this.$strengthBarTitle.innerHTML = __('Password strength');
        this.$passwordCheckList.innerHTML = `
            <li class='rule-min'>${__('Minimum 10 characters')}</li>
            <li class='rule-lowercase'>${__('One lowercase letter')}</li>
            <li class='rule-uppercase'>${__('One upper case letter')}</li>
            <li class='rule-digits'>${__('A number')}</li>
        `;

        $passwordStrengthField.classList.add('row', 'field--password-stength');
        $strengthBar.classList.add('password-stength__bar');
        this.$passwordWrapper.classList.add('form-group', 'col-12');
        this.$passwordWrapper.append(this.$passwordCheckList);
        this.$strengthBarTitle.classList.add('password-stength__title');
        this.$passwordCheckList.classList.add('password-stength__check-list');
        this.$strengthBarProgress.classList.add('password-stength__bar-progress');

        $passwordStrengthField.appendChild(this.$firstPasswordField);
        $passwordStrengthField.appendChild(this.$auditPasswordField);
        this.$firstPasswordField.appendChild(this.$fieldMessage);
        this.$passwordWrapper.append(this.$strengthBarTitle);
        $strengthBar.appendChild(this.$strengthBarProgress);
        this.$passwordWrapper.appendChild($strengthBar);
        $passwordStrengthField.appendChild(this.$passwordWrapper);

        this.$firstPasswordField.addEventListener(EVENT_NAME_PASSWORD_INPUT, () => {
            this.update(firstPasswordField.value, auditPasswordField.value);
        });

        this.$auditPasswordField.addEventListener(EVENT_NAME_PASSWORD_INPUT, () => {
            this.update(firstPasswordField.value, auditPasswordField.value);
        });

        this.update(firstPasswordField.value, auditPasswordField.value);

        return $passwordStrengthField;
    }

    update(password, auditPassword) {
        let flaggedPasswordRules = [];
        let passwordsAreEqual = false;
        let passwordIsValid = false;
        this.isValid = true;
        this.$strengthBarTitle.classList.toggle('visually-hidden', password === '');

        // Create a schema
        const schema = new PasswordValidator();

        // Add properties to it
        schema
            .is().min(10)                                // Minimum length 8
            .is().max(100)                               // Maximum length 100
            .has().uppercase()                           // Must have uppercase letters
            .has().lowercase()                           // Must have lowercase letters
            .has().digits()                              // Must have digits
            .has().not().spaces()                        // Should not have spaces
            .is().not().oneOf(this.forbiddenPasswords);  // Blacklist these values

        // Get a full list of rules which failed
        flaggedPasswordRules = schema.validate(password, { list: true });
        for (const rule of Object.keys(this.passwordRules)) {
            this.$passwordCheckList.classList.toggle(this.passwordRules[rule], !flaggedPasswordRules.includes(rule));
        }

        if (flaggedPasswordRules.includes('oneOf')) {
            this.$firstPasswordField.classList.add('field--forbidden-password');
            this.$fieldMessage.innerHTML = this.passwordRuleMessages.forbiddenPassword;
        } else {
            this.$firstPasswordField.classList.remove('field--forbidden-password');
        }

        passwordsAreEqual = (password === auditPassword);
        passwordIsValid = !flaggedPasswordRules.includes('oneOf') && !flaggedPasswordRules.includes('min') && flaggedPasswordRules.length <= 1;
        this.isValid = passwordsAreEqual && passwordIsValid;
        const inputField = this.$firstPasswordField.querySelector('#password');
        const auditField = this.$auditPasswordField.querySelector('#audit-password');
        inputField.classList.toggle('is-valid', (password === auditPassword && password !== '' && auditPassword !== ''));
        inputField.classList.toggle('is-invalid', (password !== auditPassword && password !== '' && auditPassword !== ''));
        auditField.classList.toggle('is-valid', (password === auditPassword && password !== '' && auditPassword !== ''));
        auditField.classList.toggle('is-invalid', (password !== auditPassword && password !== '' && auditPassword !== ''));

        if (password !== auditPassword && password !== '' && auditPassword !== '') {
            this.$fieldMessage.innerHTML = this.passwordRuleMessages.noMatch;
            this.$firstPasswordField.classList.add('field--password-no-match');
        } else {
            this.$fieldMessage.innerHTML = this.passwordRuleMessages.forbiddenPassword;
            this.$firstPasswordField.classList.remove('field--password-no-match');
        }

        this.entropy = entropy(password);
        this.updateStrengthBar();

        this.$context.dispatchEvent(EVENT_STEP_VALIDATE);
    }

    updateStrengthBar() {
        const entropyMiddle = (this.strongPasswordEntropy / 2);
        const barWidth = Math.min(this.entropy, this.strongPasswordEntropy) / (this.strongPasswordEntropy / 100);
        const entropyRatio = Math.min(this.entropy, this.strongPasswordEntropy) / entropyMiddle;

        this.$strengthBarProgress.style.width = `${barWidth}%`;

        if (this.entropy < entropyMiddle) {
            // First show a progresse bar from red to yellow. From 0 to 50% of the strongPasswordEntropy.
            this.$strengthBarProgress.style.backgroundColor = `#${colourHelper.averageColour('#E1141D', '#E1B914', entropyRatio)}`;
        } else {
            // Second show a progresse bar from yellow to green. From 50% of the strongPasswordEntropy to strongPasswordEntropy or higher.
            this.$strengthBarProgress.style.backgroundColor = `#${colourHelper.averageColour('#E1B914', '#4D8E51', entropyRatio - 1) }`;
        }
    }

    valid() {
        return this.isValid;
    }

    render() {
        return this.$el;
    }
}

export default PasswordStrength;
